/**
 * PermissionRoute Component
 * Route guard that checks user permissions before rendering children.
 */

import { Navigate } from 'react-router-dom'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuthStore } from '@/stores/authStore'
import { ContentLoader } from '@/components/ui/loading'

export interface PermissionRouteProps {
  /** Single permission string in format "resource:action" */
  permission?: string
  /** Array of permissions - user must have ALL of these */
  permissions?: string[]
  /** Array of permissions - user must have ANY of these */
  anyPermissions?: string[]
  children: React.ReactNode
  /** Path to redirect to when user doesn't have permission (default: "/access-denied") */
  redirectTo?: string
}

export function PermissionRoute({
  permission,
  permissions,
  anyPermissions,
  children,
  redirectTo = '/access-denied',
}: PermissionRouteProps) {
  const { can, isLoading } = usePermissions()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (isLoading) {
    return <ContentLoader />
  }

  let hasAccess: boolean

  if (permission) {
    hasAccess = can(permission)
  } else if (permissions && permissions.length > 0) {
    hasAccess = permissions.every((p) => can(p))
  } else if (anyPermissions && anyPermissions.length > 0) {
    hasAccess = anyPermissions.some((p) => can(p))
  } else {
    hasAccess = true
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

export default PermissionRoute
